<script setup>
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleAnalysisDocuments from './PostSaleAnalysisDocuments.vue';
import PostSaleAnalysisReceipt from './PostSaleAnalysisReceipt.vue';
import PostSaleCaseTopicsPanel from './PostSaleCaseTopicsPanel.vue';

defineProps({
  caseData: { type: Object, default: null },
  catalog: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  loadingCase: { type: Boolean, default: false },
  loadingTransaction: { type: Boolean, default: false },
  refreshing: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  selectedTopic: { type: Object, default: null },
  showTopicForm: { type: Boolean, default: false },
  topics: { type: Array, default: () => [] },
  transaction: { type: Object, default: null },
  updatingStatus: { type: Boolean, default: false },
});
const emit = defineEmits([
  'back',
  'closeForm',
  'create',
  'delete',
  'edit',
  'openSale',
  'openSaleDetail',
  'refresh',
  'requestCorrection',
  'save',
  'updateStatus',
]);
const { t } = useI18n();
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <LaisHeader
      :title="t('POST_SALE.TOPICS.PAGE.TITLE')"
      :subtitle="t('POST_SALE.TOPICS.PAGE.SUBTITLE')"
      :breadcrumb="[
        { label: t('POST_SALE.TITLE') },
        { label: t('POST_SALE.TOPICS.PAGE.TITLE') },
      ]"
      show-back
      :back-label="t('POST_SALE.TOPICS.ACTIONS.BACK')"
      @back="emit('back')"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="refreshing"
          @click="emit('refresh')"
        >
          {{ t('POST_SALE.ACTIONS.REFRESH') }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <main
      class="mx-auto flex w-full max-w-[1280px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
    >
      <template v-if="loadingCase">
        <div
          v-for="index in 3"
          :key="index"
          class="h-32 animate-pulse rounded-lais bg-white"
        />
      </template>
      <template v-else-if="caseData">
        <LaisCard
          :title="
            caseData.transaction?.title || caseData.transaction?.reference
          "
          prefix-icon="i-lucide-house"
        >
          <p class="text-sm text-lais-text-body">
            {{
              caseData.transaction?.address?.endereco ||
              t('POST_SALE.COMMON.ADDRESS_UNAVAILABLE')
            }}
          </p>
          <div class="mt-4 flex flex-wrap gap-3">
            <LaisPrimaryButton variant="ghost" @click="emit('openSale')">
              {{ t('POST_SALE.TOPICS.ACTIONS.VIEW_SALE') }}
            </LaisPrimaryButton>
            <LaisPrimaryButton
              v-if="caseData.permissions?.can_request_correction"
              variant="ghost"
              icon="i-lucide-undo-2"
              @click="emit('requestCorrection')"
            >
              {{ t('POST_SALE.ACTIONS.RETURN_SALE') }}
            </LaisPrimaryButton>
          </div>
        </LaisCard>

        <PostSaleAnalysisDocuments
          :loading="loadingTransaction"
          :transaction="transaction"
          @open-detail="emit('openSaleDetail', $event)"
        />
        <PostSaleAnalysisReceipt
          :loading="loadingTransaction"
          :transaction="transaction"
        />
        <PostSaleCaseTopicsPanel
          :can-manage="caseData.permissions?.can_manage"
          :catalog="catalog"
          :loading="loading"
          :saving="saving"
          :selected-topic="selectedTopic"
          :show-form="showTopicForm"
          :topics="topics"
          @close-form="emit('closeForm')"
          @create="emit('create')"
          @delete="emit('delete', $event)"
          @edit="emit('edit', $event)"
          @request-correction="emit('requestCorrection', $event)"
          @save="emit('save', $event)"
        />
      </template>
    </main>

    <footer
      v-if="caseData?.permissions?.can_manage && !loadingCase"
      class="sticky bottom-0 border-t border-lais-border-soft bg-white px-4 py-4 sm:px-6 lg:px-10"
    >
      <div class="mx-auto flex w-full max-w-[1280px] justify-end">
        <LaisPrimaryButton
          icon="i-lucide-arrow-right"
          :is-loading="updatingStatus"
          @click="emit('updateStatus')"
        >
          {{ t('POST_SALE.ACTIONS.UPDATE_STATUS') }}
        </LaisPrimaryButton>
      </div>
    </footer>
  </div>
</template>
